import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Search, 
  RefreshCw,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Car,
  AlertCircle,
  Loader2,
  Image,
  Plus,
  Trash2,
  Truck,
  Building2,
  Users,
  FileText,
  Edit2,
  Eye,
  ClipboardList,
  Wrench,
  UserPlus,
  Lock,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  confirmed: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  in_progress: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  cancelled: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' }
};

const statusLabels = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const paymentStatusColors = {
  paid: { bg: 'bg-green-50', text: 'text-green-700' },
  unpaid: { bg: 'bg-gray-50', text: 'text-gray-600' },
  refunded: { bg: 'bg-orange-50', text: 'text-orange-700' }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [isAdmin, setIsAdmin] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected booking for detail view
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Gallery state
  const [activeTab, setActiveTab] = useState('bookings');
  const [galleryItems, setGalleryItems] = useState([]);
  const [showAddGallery, setShowAddGallery] = useState(false);
  const [newGalleryItem, setNewGalleryItem] = useState({
    title: '',
    description: '',
    before_image: '',
    after_image: '',
    vehicle_make: '',
    vehicle_model: '',
    service_type: ''
  });

  // Fleet state
  const [fleetEnquiries, setFleetEnquiries] = useState([]);
  const [fleetStats, setFleetStats] = useState(null);
  const [selectedFleetEnquiry, setSelectedFleetEnquiry] = useState(null);
  const [fleetSubTab, setFleetSubTab] = useState('enquiries'); // 'enquiries' | 'vehicles'
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [selectedFleetVehicle, setSelectedFleetVehicle] = useState(null);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    fleet_customer_id: '',
    registration: '',
    make: '',
    model: '',
    vehicle_type: 'sedan',
    colour: '',
    year: '',
    vin: '',
    notes: ''
  });
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [showAddInspection, setShowAddInspection] = useState(false);
  const [newInspection, setNewInspection] = useState({
    mileage: '',
    exterior_condition: 'good',
    interior_condition: 'good',
    body_damage: '',
    warning_lights: '',
    tyre_condition: '',
    windscreen_condition: '',
    notes: ''
  });
  const [addingInspection, setAddingInspection] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({
    service_type: 'valet',
    description: '',
    mileage: '',
    cost: '',
    booking_reference: '',
    notes: ''
  });
  const [addingService, setAddingService] = useState(false);

  // Workshop Staff state
  const [workshopStaff, setWorkshopStaff] = useState([]);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [newStaff, setNewStaff] = useState({
    email: '',
    name: '',
    password: '',
    role: 'technician'
  });
  const [addingStaff, setAddingStaff] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, statusFilter, paymentFilter, searchQuery]);

  const checkAdminAccess = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/check`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (!data.authenticated) {
        navigate('/');
        toast.error('Please sign in to access the admin dashboard');
        return;
      }
      
      if (!data.is_admin) {
        navigate('/');
        toast.error('You do not have admin access');
        return;
      }
      
      setIsAdmin(true);
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/');
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchData = async () => {
    try {
      // Fetch stats, bookings, gallery, fleet, and workshop staff in parallel
      const [statsRes, bookingsRes, galleryRes, fleetStatsRes, fleetEnquiriesRes, workshopStaffRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/bookings?${new URLSearchParams({
          ...(statusFilter && { status: statusFilter }),
          ...(paymentFilter && { payment_status: paymentFilter }),
          ...(searchQuery && { search: searchQuery })
        })}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/gallery`),
        fetch(`${API_URL}/api/admin/fleet-stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/fleet-enquiries`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/workshop-staff`, { credentials: 'include' })
      ]);
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setBookings(data.bookings);
      }
      
      if (galleryRes.ok) {
        const data = await galleryRes.json();
        setGalleryItems(data.items);
      }
      
      if (fleetStatsRes.ok) {
        setFleetStats(await fleetStatsRes.json());
      }
      
      if (fleetEnquiriesRes.ok) {
        const data = await fleetEnquiriesRes.json();
        setFleetEnquiries(data.enquiries);
      }
      
      if (workshopStaffRes.ok) {
        const data = await workshopStaffRes.json();
        setWorkshopStaff(data.staff || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };
  
  const handleAddGalleryItem = async () => {
    if (!newGalleryItem.title || !newGalleryItem.before_image || !newGalleryItem.after_image) {
      toast.error('Please fill in title and both image URLs');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newGalleryItem)
      });
      
      if (response.ok) {
        toast.success('Gallery item added');
        setShowAddGallery(false);
        setNewGalleryItem({
          title: '',
          description: '',
          before_image: '',
          after_image: '',
          vehicle_make: '',
          vehicle_model: '',
          service_type: ''
        });
        fetchData();
      } else {
        toast.error('Failed to add gallery item');
      }
    } catch (error) {
      console.error('Add gallery error:', error);
      toast.error('Failed to add gallery item');
    }
  };
  
  const handleDeleteGalleryItem = async (itemId) => {
    if (!window.confirm('Delete this gallery item?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gallery/${itemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Gallery item deleted');
        fetchData();
      } else {
        toast.error('Failed to delete gallery item');
      }
    } catch (error) {
      console.error('Delete gallery error:', error);
      toast.error('Failed to delete gallery item');
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Status updated to ${statusLabels[newStatus]}`);
        fetchData();
        if (selectedBooking?.id === bookingId) {
          setSelectedBooking({ ...selectedBooking, status: newStatus });
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Status update failed:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr + 'T12:00:00');
      return date.toLocaleDateString('en-ZA', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (isoStr) => {
    try {
      const date = new Date(isoStr);
      return date.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoStr;
    }
  };

  // Fleet Vehicle Management Functions
  const searchFleetVehicles = async (query) => {
    if (!query || query.length < 2) {
      setFleetVehicles([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/fleet-vehicles/search?registration=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFleetVehicles(data.vehicles || []);
      }
    } catch (error) {
      console.error('Search fleet vehicles error:', error);
    }
  };

  const fetchFleetCustomerVehicles = async (customerId) => {
    try {
      const response = await fetch(`${API_URL}/api/fleet/vehicles/${customerId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFleetVehicles(data.vehicles || []);
      }
    } catch (error) {
      console.error('Fetch fleet vehicles error:', error);
    }
  };

  const fetchFleetVehicle = async (vehicleId) => {
    try {
      const response = await fetch(`${API_URL}/api/fleet/vehicle/${vehicleId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const vehicle = await response.json();
        setSelectedFleetVehicle(vehicle);
      }
    } catch (error) {
      console.error('Fetch fleet vehicle error:', error);
    }
  };

  const handleAddFleetVehicle = async () => {
    if (!newVehicle.fleet_customer_id || !newVehicle.registration || !newVehicle.make || !newVehicle.model) {
      toast.error('Please fill in customer ID, registration, make and model');
      return;
    }
    setAddingVehicle(true);
    try {
      const response = await fetch(`${API_URL}/api/fleet/vehicles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newVehicle,
          year: newVehicle.year ? parseInt(newVehicle.year) : null
        })
      });
      
      if (response.ok) {
        toast.success('Vehicle added to fleet');
        setShowAddVehicle(false);
        setNewVehicle({
          fleet_customer_id: '',
          registration: '',
          make: '',
          model: '',
          vehicle_type: 'sedan',
          colour: '',
          year: '',
          vin: '',
          notes: ''
        });
        if (newVehicle.fleet_customer_id) {
          fetchFleetCustomerVehicles(newVehicle.fleet_customer_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add vehicle');
      }
    } catch (error) {
      console.error('Add vehicle error:', error);
      toast.error('Failed to add vehicle');
    } finally {
      setAddingVehicle(false);
    }
  };

  const handleDeleteFleetVehicle = async (vehicleId) => {
    if (!window.confirm('Delete this vehicle from the fleet?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/fleet/vehicle/${vehicleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Vehicle removed from fleet');
        setSelectedFleetVehicle(null);
        setFleetVehicles(prev => prev.filter(v => v.id !== vehicleId));
      } else {
        toast.error('Failed to delete vehicle');
      }
    } catch (error) {
      console.error('Delete vehicle error:', error);
      toast.error('Failed to delete vehicle');
    }
  };

  const handleAddInspection = async () => {
    if (!selectedFleetVehicle) return;
    setAddingInspection(true);
    try {
      const response = await fetch(`${API_URL}/api/fleet/vehicle/${selectedFleetVehicle.id}/inspection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          vehicle_id: selectedFleetVehicle.id,
          ...newInspection,
          mileage: newInspection.mileage ? parseInt(newInspection.mileage) : null
        })
      });
      
      if (response.ok) {
        toast.success('Inspection record added');
        setShowAddInspection(false);
        setNewInspection({
          mileage: '',
          exterior_condition: 'good',
          interior_condition: 'good',
          body_damage: '',
          warning_lights: '',
          tyre_condition: '',
          windscreen_condition: '',
          notes: ''
        });
        fetchFleetVehicle(selectedFleetVehicle.id);
      } else {
        toast.error('Failed to add inspection');
      }
    } catch (error) {
      console.error('Add inspection error:', error);
      toast.error('Failed to add inspection');
    } finally {
      setAddingInspection(false);
    }
  };

  const handleAddServiceRecord = async () => {
    if (!selectedFleetVehicle) return;
    setAddingService(true);
    try {
      const response = await fetch(`${API_URL}/api/fleet/vehicle/${selectedFleetVehicle.id}/service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newService,
          mileage: newService.mileage ? parseInt(newService.mileage) : null,
          cost: newService.cost ? parseFloat(newService.cost) : null
        })
      });
      
      if (response.ok) {
        toast.success('Service record added');
        setShowAddService(false);
        setNewService({
          service_type: 'valet',
          description: '',
          mileage: '',
          cost: '',
          booking_reference: '',
          notes: ''
        });
        fetchFleetVehicle(selectedFleetVehicle.id);
      } else {
        toast.error('Failed to add service record');
      }
    } catch (error) {
      console.error('Add service record error:', error);
      toast.error('Failed to add service record');
    } finally {
      setAddingService(false);
    }
  };

  // Workshop Staff Management Functions
  const handleAddWorkshopStaff = async () => {
    if (!newStaff.email || !newStaff.name || !newStaff.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    setAddingStaff(true);
    try {
      const response = await fetch(`${API_URL}/api/workshop/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newStaff)
      });
      
      if (response.ok) {
        toast.success(`Staff member ${newStaff.name} registered!`);
        setShowAddStaff(false);
        setNewStaff({ email: '', name: '', password: '', role: 'technician' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add staff member');
      }
    } catch (error) {
      console.error('Add staff error:', error);
      toast.error('Failed to add staff member');
    } finally {
      setAddingStaff(false);
    }
  };

  const handleDeactivateStaff = async (staffId, name) => {
    if (!window.confirm(`Deactivate ${name}? They will no longer be able to access the workshop portal.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/workshop-staff/${staffId}/deactivate`, {
        method: 'PATCH',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(`${name} has been deactivated`);
        fetchData();
      } else {
        toast.error('Failed to deactivate staff member');
      }
    } catch (error) {
      console.error('Deactivate staff error:', error);
      toast.error('Failed to deactivate staff member');
    }
  };

  if (authLoading || checkingAccess) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center pt-20">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <main data-testid="admin-dashboard" className="bg-[#f5f5f7] min-h-screen pt-20 pb-12">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-[#1d1d1f]">Admin Dashboard</h1>
            <p className="text-sm text-[#86868b]">Manage bookings and gallery</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-[#d2d2d7] text-sm text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'bookings'
                ? 'bg-[#C41E3A] text-white'
                : 'bg-white text-[#424245] hover:bg-[#f5f5f7]'
            }`}
            data-testid="tab-bookings"
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('fleet')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'fleet'
                ? 'bg-[#C41E3A] text-white'
                : 'bg-white text-[#424245] hover:bg-[#f5f5f7]'
            }`}
            data-testid="tab-fleet"
          >
            <Truck className="w-4 h-4 inline mr-2" />
            Fleet
            {fleetStats?.pending > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {fleetStats.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('workshop')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'workshop'
                ? 'bg-[#C41E3A] text-white'
                : 'bg-white text-[#424245] hover:bg-[#f5f5f7]'
            }`}
            data-testid="tab-workshop"
          >
            <Wrench className="w-4 h-4 inline mr-2" />
            Workshop Staff
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'gallery'
                ? 'bg-[#C41E3A] text-white'
                : 'bg-white text-[#424245] hover:bg-[#f5f5f7]'
            }`}
            data-testid="tab-gallery"
          >
            <Image className="w-4 h-4 inline mr-2" />
            Gallery
          </button>
        </div>

        {activeTab === 'bookings' && (
          <>
            {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-5 border border-[#d2d2d7]"
              data-testid="stat-total"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#f5f5f7] flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#1d1d1f]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#1d1d1f]">{stats.total_bookings}</p>
                  <p className="text-xs text-[#86868b]">Total Bookings</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-5 border border-[#d2d2d7]"
              data-testid="stat-pending"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#1d1d1f]">{stats.pending_bookings}</p>
                  <p className="text-xs text-[#86868b]">Pending</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-5 border border-[#d2d2d7]"
              data-testid="stat-confirmed"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#1d1d1f]">{stats.confirmed_bookings}</p>
                  <p className="text-xs text-[#86868b]">Confirmed</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-5 border border-[#d2d2d7]"
              data-testid="stat-revenue"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#1d1d1f]">R{stats.total_revenue.toLocaleString()}</p>
                  <p className="text-xs text-[#86868b]">Revenue</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-[#d2d2d7] mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b]" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or vehicle..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#D4AF37]"
                data-testid="search-input"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#D4AF37] cursor-pointer"
                data-testid="status-filter"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b] pointer-events-none" />
            </div>

            {/* Payment Filter */}
            <div className="relative">
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#D4AF37] cursor-pointer"
                data-testid="payment-filter"
              >
                <option value="">All Payments</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b] pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Bookings List */}
        <div className="bg-white rounded-xl border border-[#d2d2d7] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
              <p className="text-[#86868b]">No bookings found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="bookings-table">
                <thead className="bg-[#f5f5f7] border-b border-[#d2d2d7]">
                  <tr>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Customer</th>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Service</th>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Vehicle</th>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Date</th>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Payment</th>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Amount</th>
                    <th className="text-left text-xs font-medium text-[#86868b] px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#d2d2d7]">
                  {bookings.map((booking) => (
                    <tr 
                      key={booking.id} 
                      className="hover:bg-[#f5f5f7]/50 transition-colors"
                      data-testid={`booking-row-${booking.id}`}
                    >
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-[#1d1d1f]">{booking.customer_name}</p>
                        <p className="text-xs text-[#86868b]">{booking.customer_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-[#1d1d1f]">{booking.package_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-[#1d1d1f]">{booking.vehicle_make} {booking.vehicle_model}</p>
                        <p className="text-xs text-[#86868b] capitalize">{booking.vehicle_type}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-[#1d1d1f]">{formatDate(booking.preferred_date)}</p>
                        <p className="text-xs text-[#86868b]">{booking.preferred_time}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusColors[booking.status]?.bg} ${statusColors[booking.status]?.text}`}>
                          {statusLabels[booking.status] || booking.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${paymentStatusColors[booking.payment_status]?.bg} ${paymentStatusColors[booking.payment_status]?.text}`}>
                          {booking.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-[#1d1d1f]">R{booking.total_price?.toLocaleString()}</p>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedBooking(booking)}
                          className="text-xs text-[#D4AF37] hover:text-[#c4a030] font-medium"
                          data-testid={`view-booking-${booking.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}
        
        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="bg-white rounded-xl border border-[#d2d2d7] overflow-hidden">
            <div className="p-4 border-b border-[#d2d2d7] flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1d1d1f]">Gallery Items</h2>
              <button
                onClick={() => setShowAddGallery(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#a01830] transition-colors"
                data-testid="add-gallery-btn"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>
            
            {galleryItems.length === 0 ? (
              <div className="p-12 text-center">
                <Image className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
                <p className="text-[#86868b]">No gallery items yet</p>
                <p className="text-xs text-[#86868b] mt-1">Add before/after photos to showcase your work</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {galleryItems.map((item) => (
                  <div key={item.id} className="bg-[#f5f5f7] rounded-xl p-4" data-testid={`gallery-admin-${item.id}`}>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <p className="text-[10px] text-[#86868b] mb-1">Before</p>
                        <img src={item.before_image} alt="Before" className="w-full h-20 object-cover rounded-lg" />
                      </div>
                      <div>
                        <p className="text-[10px] text-[#86868b] mb-1">After</p>
                        <img src={item.after_image} alt="After" className="w-full h-20 object-cover rounded-lg" />
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-[#1d1d1f] truncate">{item.title}</h3>
                    {item.vehicle_make && (
                      <p className="text-xs text-[#86868b]">{item.vehicle_make} {item.vehicle_model}</p>
                    )}
                    <button
                      onClick={() => handleDeleteGalleryItem(item.id)}
                      className="mt-2 flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                      data-testid={`delete-gallery-${item.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fleet Tab */}
        {activeTab === 'fleet' && (
          <>
            {/* Fleet Stats */}
            {fleetStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl p-5 border border-[#d2d2d7]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1d1d1f]">{fleetStats.total_enquiries}</p>
                      <p className="text-xs text-[#86868b]">Total Enquiries</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#d2d2d7]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1d1d1f]">{fleetStats.pending}</p>
                      <p className="text-xs text-[#86868b]">Pending</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#d2d2d7]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1d1d1f]">{fleetStats.total_vehicles}</p>
                      <p className="text-xs text-[#86868b]">Total Vehicles</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 border border-[#d2d2d7]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1d1d1f]">{fleetStats.partners}</p>
                      <p className="text-xs text-[#86868b]">Fleet Partners</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fleet Sub-Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setFleetSubTab('enquiries')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  fleetSubTab === 'enquiries'
                    ? 'bg-[#1d1d1f] text-white'
                    : 'bg-white text-[#424245] hover:bg-[#f5f5f7] border border-[#d2d2d7]'
                }`}
                data-testid="fleet-subtab-enquiries"
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Enquiries
              </button>
              <button
                onClick={() => setFleetSubTab('vehicles')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  fleetSubTab === 'vehicles'
                    ? 'bg-[#1d1d1f] text-white'
                    : 'bg-white text-[#424245] hover:bg-[#f5f5f7] border border-[#d2d2d7]'
                }`}
                data-testid="fleet-subtab-vehicles"
              >
                <Car className="w-4 h-4 inline mr-2" />
                Vehicle Records
              </button>
            </div>

            {/* Fleet Enquiries Sub-Tab */}
            {fleetSubTab === 'enquiries' && (
              <div className="bg-white rounded-xl border border-[#d2d2d7] overflow-hidden">
                <div className="p-4 border-b border-[#d2d2d7]">
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">Fleet Enquiries</h2>
                </div>
                
                {fleetEnquiries.length === 0 ? (
                  <div className="p-12 text-center">
                    <Truck className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
                    <p className="text-[#86868b]">No fleet enquiries yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#d2d2d7]">
                    {fleetEnquiries.map((enquiry) => (
                      <div 
                        key={enquiry.id} 
                        className="p-4 hover:bg-[#f5f5f7] cursor-pointer transition-colors"
                        onClick={() => setSelectedFleetEnquiry(enquiry)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-[#1d1d1f]">{enquiry.company_name}</h3>
                              {enquiry.is_partner && (
                                <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">Partner</span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                enquiry.status === 'completed' ? 'bg-green-100 text-green-700' :
                                enquiry.status === 'booking_confirmed' ? 'bg-blue-100 text-blue-700' :
                                enquiry.status.includes('quote') ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {enquiry.status.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-[#86868b]">
                              {enquiry.contact_person} • {enquiry.total_vehicles} vehicles • {enquiry.frequency.replace(/_/g, ' ')}
                            </p>
                            <p className="text-xs text-[#86868b] mt-1">
                              {enquiry.reference_number}
                            </p>
                          </div>
                          <div className="text-right">
                            {enquiry.estimated_price && (
                              <p className="text-lg font-semibold text-[#C41E3A]">R{enquiry.estimated_price.toLocaleString()}</p>
                            )}
                            {enquiry.requires_quote && !enquiry.estimated_price && (
                              <p className="text-sm text-amber-600">Quote Required</p>
                            )}
                            <p className="text-xs text-[#86868b]">
                              {new Date(enquiry.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Fleet Vehicle Records Sub-Tab */}
            {fleetSubTab === 'vehicles' && (
              <div className="space-y-4">
                {/* Search & Add Controls */}
                <div className="bg-white rounded-xl border border-[#d2d2d7] p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-xs text-[#86868b] mb-1">Search by Registration</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b]" />
                        <input
                          type="text"
                          placeholder="e.g. CA 123 456"
                          value={vehicleSearch}
                          onChange={(e) => {
                            setVehicleSearch(e.target.value);
                            searchFleetVehicles(e.target.value);
                          }}
                          className="w-full pl-10 pr-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                          data-testid="fleet-vehicle-search"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-[#86868b] mb-1">&nbsp;</label>
                      <button
                        onClick={() => setShowAddVehicle(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#a01830] transition-colors"
                        data-testid="add-fleet-vehicle-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Add Vehicle
                      </button>
                    </div>
                  </div>
                </div>

                {/* Fleet Customers from Enquiries */}
                <div className="bg-white rounded-xl border border-[#d2d2d7] overflow-hidden">
                  <div className="p-4 border-b border-[#d2d2d7]">
                    <h3 className="font-semibold text-[#1d1d1f]">Fleet Customers</h3>
                    <p className="text-xs text-[#86868b]">Click on a customer to manage their vehicle records</p>
                  </div>
                  
                  {fleetEnquiries.filter(e => e.is_partner || e.status === 'booking_confirmed' || e.status === 'completed').length === 0 ? (
                    <div className="p-8 text-center">
                      <Building2 className="w-10 h-10 text-[#86868b] mx-auto mb-3" />
                      <p className="text-sm text-[#86868b]">No fleet customers yet</p>
                      <p className="text-xs text-[#86868b] mt-1">Customers appear here after their enquiry status is updated to &quot;Confirmed&quot; or &quot;Completed&quot;</p>
                      {fleetEnquiries.length > 0 && (
                        <p className="text-xs text-blue-500 mt-2">Tip: Go to Enquiries tab and update an enquiry status to enable vehicle tracking</p>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-[#d2d2d7]">
                      {fleetEnquiries.filter(e => e.is_partner || e.status === 'booking_confirmed' || e.status === 'completed').map((customer) => (
                        <div 
                          key={customer.id} 
                          className="p-4 hover:bg-[#f5f5f7] cursor-pointer transition-colors flex items-center justify-between"
                          onClick={() => {
                            fetchFleetCustomerVehicles(customer.id);
                            setNewVehicle(prev => ({ ...prev, fleet_customer_id: customer.id }));
                          }}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-[#1d1d1f]">{customer.company_name}</h4>
                              {customer.is_partner && (
                                <span className="bg-purple-100 text-purple-700 text-[10px] px-2 py-0.5 rounded-full">Partner</span>
                              )}
                            </div>
                            <p className="text-xs text-[#86868b]">{customer.contact_person} • {customer.total_vehicles} vehicles</p>
                          </div>
                          <ChevronDown className="w-4 h-4 text-[#86868b] -rotate-90" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Vehicle Search Results */}
                {fleetVehicles.length > 0 && (
                  <div className="bg-white rounded-xl border border-[#d2d2d7] overflow-hidden">
                    <div className="p-4 border-b border-[#d2d2d7]">
                      <h3 className="font-semibold text-[#1d1d1f]">Vehicles ({fleetVehicles.length})</h3>
                    </div>
                    <div className="divide-y divide-[#d2d2d7]">
                      {fleetVehicles.map((vehicle) => (
                        <div 
                          key={vehicle.id}
                          className="p-4 hover:bg-[#f5f5f7] cursor-pointer transition-colors"
                          onClick={() => fetchFleetVehicle(vehicle.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#f5f5f7] flex items-center justify-center">
                                <Car className="w-5 h-5 text-[#424245]" />
                              </div>
                              <div>
                                <p className="font-semibold text-[#1d1d1f]">{vehicle.registration}</p>
                                <p className="text-sm text-[#86868b]">{vehicle.make} {vehicle.model}</p>
                              </div>
                            </div>
                            <div className="text-right text-xs text-[#86868b]">
                              {vehicle.service_history?.length || 0} services
                              <br />
                              {vehicle.inspections?.length || 0} inspections
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Workshop Staff Tab */}
        {activeTab === 'workshop' && (
          <div className="space-y-6">
            {/* Workshop Portal Link */}
            <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B1538] rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold mb-1">Workshop Portal</h2>
                  <p className="text-white/80 text-sm">Staff members access jobs and update status here</p>
                </div>
                <a
                  href="/workshop/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white text-[#C41E3A] rounded-lg font-medium hover:bg-white/90 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open Portal
                </a>
              </div>
            </div>

            {/* Staff Management */}
            <div className="bg-white rounded-xl border border-[#d2d2d7] overflow-hidden">
              <div className="p-4 border-b border-[#d2d2d7] flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">Workshop Staff</h2>
                  <p className="text-xs text-[#86868b]">Manage staff who can access the workshop portal</p>
                </div>
                <button
                  onClick={() => setShowAddStaff(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#a01830] transition-colors"
                  data-testid="add-workshop-staff-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Staff
                </button>
              </div>
              
              {workshopStaff.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
                  <p className="text-[#86868b] mb-2">No workshop staff yet</p>
                  <p className="text-sm text-[#86868b]">Add staff members to give them access to the workshop portal</p>
                </div>
              ) : (
                <div className="divide-y divide-[#d2d2d7]">
                  {workshopStaff.map((member) => (
                    <div key={member.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#f5f5f7] flex items-center justify-center">
                          <Wrench className="w-5 h-5 text-[#424245]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1d1d1f]">{member.name}</p>
                          <p className="text-sm text-[#86868b]">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.role === 'manager' ? 'bg-purple-100 text-purple-700' :
                          member.role === 'supervisor' ? 'bg-blue-100 text-blue-700' :
                          'bg-zinc-100 text-zinc-700'
                        }`}>
                          {member.role}
                        </span>
                        {member.is_active ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Active</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">Inactive</span>
                        )}
                        {member.is_active && (
                          <button
                            onClick={() => handleDeactivateStaff(member.id, member.name)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-medium text-blue-900 mb-2">How it works</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Add staff members with their email and a password</li>
                <li>Share the workshop portal link: <code className="bg-blue-100 px-1 rounded">/workshop/login</code></li>
                <li>Staff log in and see the day&apos;s jobs</li>
                <li>They update job status (Check In → Start → Complete)</li>
                <li>Customers receive SMS notifications automatically</li>
              </ol>
            </div>
          </div>
        )}

        {/* Fleet Enquiry Detail Modal */}
        {selectedFleetEnquiry && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedFleetEnquiry(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[#d2d2d7] flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">{selectedFleetEnquiry.company_name}</h2>
                  <p className="text-sm text-[#86868b]">{selectedFleetEnquiry.reference_number}</p>
                </div>
                <button
                  onClick={() => setSelectedFleetEnquiry(null)}
                  className="text-[#86868b] hover:text-[#1d1d1f] text-xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div>
                  <h3 className="text-xs font-medium text-[#86868b] mb-3">CONTACT</h3>
                  <div className="space-y-2 text-sm text-[#424245]">
                    <p><strong>Contact Person:</strong> {selectedFleetEnquiry.contact_person}</p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${selectedFleetEnquiry.cell_number}`} className="text-[#C41E3A]">{selectedFleetEnquiry.cell_number}</a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${selectedFleetEnquiry.email}`} className="text-[#C41E3A]">{selectedFleetEnquiry.email}</a>
                    </p>
                  </div>
                </div>

                {/* Fleet Info */}
                <div>
                  <h3 className="text-xs font-medium text-[#86868b] mb-3">FLEET DETAILS</h3>
                  <div className="bg-[#f5f5f7] rounded-xl p-4 space-y-2 text-sm">
                    <p><strong>Total Vehicles:</strong> {selectedFleetEnquiry.total_vehicles}</p>
                    <p><strong>Frequency:</strong> {selectedFleetEnquiry.frequency?.replace(/_/g, ' ')}</p>
                    <p><strong>Location:</strong> {selectedFleetEnquiry.service_location?.replace(/_/g, ' ')}</p>
                    <p><strong>Condition:</strong> {selectedFleetEnquiry.condition?.replace(/_/g, ' ')}</p>
                    {selectedFleetEnquiry.vehicle_details && (
                      <p><strong>Vehicle Details:</strong> {selectedFleetEnquiry.vehicle_details}</p>
                    )}
                  </div>
                </div>

                {/* Pricing */}
                <div>
                  <h3 className="text-xs font-medium text-[#86868b] mb-3">PRICING</h3>
                  <div className="bg-[#1d1d1f] rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[#86868b]">Estimated Price</p>
                        <p className="text-2xl font-bold text-[#C41E3A]">
                          {selectedFleetEnquiry.estimated_price 
                            ? `R${selectedFleetEnquiry.estimated_price.toLocaleString()}`
                            : 'Quote Required'}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedFleetEnquiry.status === 'completed' ? 'bg-green-500 text-white' :
                        selectedFleetEnquiry.status === 'booking_confirmed' ? 'bg-blue-500 text-white' :
                        'bg-zinc-700 text-zinc-300'
                      }`}>
                        {selectedFleetEnquiry.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedFleetEnquiry.notes && (
                  <div>
                    <h3 className="text-xs font-medium text-[#86868b] mb-2">NOTES</h3>
                    <p className="text-sm text-[#424245] bg-[#f5f5f7] rounded-lg p-3">{selectedFleetEnquiry.notes}</p>
                  </div>
                )}

                {/* WhatsApp Button */}
                <a
                  href={`https://wa.me/${selectedFleetEnquiry.whatsapp_number?.replace(/\D/g, '') || selectedFleetEnquiry.cell_number?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${selectedFleetEnquiry.contact_person}, regarding your fleet enquiry ${selectedFleetEnquiry.reference_number}...`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#25D366] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Contact on WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Fleet Vehicle Modal */}
        {showAddVehicle && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddVehicle(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="add-fleet-vehicle-modal"
            >
              <div className="p-6 border-b border-[#d2d2d7]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">Add Fleet Vehicle</h2>
                  <button onClick={() => setShowAddVehicle(false)} className="text-[#86868b] hover:text-[#1d1d1f]">
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Fleet Customer *</label>
                  <select
                    value={newVehicle.fleet_customer_id}
                    onChange={(e) => setNewVehicle({ ...newVehicle, fleet_customer_id: e.target.value })}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  >
                    <option value="">Select a fleet customer</option>
                    {fleetEnquiries.map((enquiry) => (
                      <option key={enquiry.id} value={enquiry.id}>{enquiry.company_name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Registration Number *</label>
                  <input
                    type="text"
                    value={newVehicle.registration}
                    onChange={(e) => setNewVehicle({ ...newVehicle, registration: e.target.value.toUpperCase() })}
                    placeholder="CA 123 456"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Make *</label>
                    <input
                      type="text"
                      value={newVehicle.make}
                      onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                      placeholder="Toyota"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Model *</label>
                    <input
                      type="text"
                      value={newVehicle.model}
                      onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                      placeholder="Hilux"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Vehicle Type</label>
                    <select
                      value={newVehicle.vehicle_type}
                      onChange={(e) => setNewVehicle({ ...newVehicle, vehicle_type: e.target.value })}
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    >
                      <option value="sedan">Sedan/Hatchback</option>
                      <option value="suv">SUV</option>
                      <option value="bakkie">Bakkie/Pickup</option>
                      <option value="van">Van</option>
                      <option value="truck">Truck</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Year</label>
                    <input
                      type="number"
                      value={newVehicle.year}
                      onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                      placeholder="2024"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Colour</label>
                  <input
                    type="text"
                    value={newVehicle.colour}
                    onChange={(e) => setNewVehicle({ ...newVehicle, colour: e.target.value })}
                    placeholder="White"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">VIN (optional)</label>
                  <input
                    type="text"
                    value={newVehicle.vin}
                    onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })}
                    placeholder="Vehicle Identification Number"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Notes</label>
                  <textarea
                    value={newVehicle.notes}
                    onChange={(e) => setNewVehicle({ ...newVehicle, notes: e.target.value })}
                    placeholder="Any additional notes"
                    rows={2}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A] resize-none"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-[#d2d2d7]">
                <button
                  onClick={handleAddFleetVehicle}
                  disabled={addingVehicle || !newVehicle.fleet_customer_id || !newVehicle.registration || !newVehicle.make || !newVehicle.model}
                  className="w-full bg-[#C41E3A] text-white py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingVehicle ? 'Adding...' : 'Add Vehicle'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Fleet Vehicle Detail Modal */}
        {selectedFleetVehicle && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedFleetVehicle(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="fleet-vehicle-detail-modal"
            >
              <div className="p-6 border-b border-[#d2d2d7] flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#1d1d1f]">{selectedFleetVehicle.registration}</h2>
                  <p className="text-sm text-[#86868b]">{selectedFleetVehicle.make} {selectedFleetVehicle.model} {selectedFleetVehicle.year && `(${selectedFleetVehicle.year})`}</p>
                </div>
                <button onClick={() => setSelectedFleetVehicle(null)} className="text-[#86868b] hover:text-[#1d1d1f] text-xl">
                  ✕
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Vehicle Details */}
                <div className="bg-[#f5f5f7] rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-[#86868b]">Type</p>
                    <p className="font-medium text-[#1d1d1f]">{selectedFleetVehicle.vehicle_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#86868b]">Colour</p>
                    <p className="font-medium text-[#1d1d1f]">{selectedFleetVehicle.colour || '-'}</p>
                  </div>
                  {selectedFleetVehicle.vin && (
                    <div className="col-span-2">
                      <p className="text-xs text-[#86868b]">VIN</p>
                      <p className="font-medium text-[#1d1d1f] font-mono text-xs">{selectedFleetVehicle.vin}</p>
                    </div>
                  )}
                </div>

                {/* Service History */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-medium text-[#86868b] uppercase">Service History ({selectedFleetVehicle.service_history?.length || 0})</h3>
                    <button
                      onClick={() => setShowAddService(true)}
                      className="text-xs text-[#C41E3A] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Service
                    </button>
                  </div>
                  {selectedFleetVehicle.service_history?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedFleetVehicle.service_history.slice().reverse().map((service, idx) => (
                        <div key={idx} className="bg-[#f5f5f7] rounded-lg p-3 text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              service.service_type === 'valet' ? 'bg-purple-100 text-purple-700' :
                              service.service_type === 'mechanical' ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {service.service_type}
                            </span>
                            <span className="text-xs text-[#86868b]">{new Date(service.date).toLocaleDateString()}</span>
                          </div>
                          <p className="text-[#424245]">{service.description || 'No description'}</p>
                          {service.cost && <p className="text-xs text-green-600 mt-1">R{service.cost.toLocaleString()}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#86868b] italic">No service records yet</p>
                  )}
                </div>

                {/* Inspections */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-medium text-[#86868b] uppercase">Inspections ({selectedFleetVehicle.inspections?.length || 0})</h3>
                    <button
                      onClick={() => setShowAddInspection(true)}
                      className="text-xs text-[#C41E3A] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Inspection
                    </button>
                  </div>
                  {selectedFleetVehicle.inspections?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedFleetVehicle.inspections.slice().reverse().map((inspection, idx) => (
                        <div key={idx} className="bg-[#f5f5f7] rounded-lg p-3 text-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-[#86868b]">{new Date(inspection.date).toLocaleDateString()}</span>
                            {inspection.mileage && <span className="text-xs bg-zinc-200 px-2 py-0.5 rounded">{inspection.mileage.toLocaleString()} km</span>}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <span className={`w-2 h-2 rounded-full ${inspection.exterior_condition === 'good' ? 'bg-green-500' : inspection.exterior_condition === 'fair' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                              <span>Exterior: {inspection.exterior_condition}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className={`w-2 h-2 rounded-full ${inspection.interior_condition === 'good' ? 'bg-green-500' : inspection.interior_condition === 'fair' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                              <span>Interior: {inspection.interior_condition}</span>
                            </div>
                          </div>
                          {inspection.notes && <p className="text-xs text-[#86868b] mt-2">{inspection.notes}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#86868b] italic">No inspection records yet</p>
                  )}
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => handleDeleteFleetVehicle(selectedFleetVehicle.id)}
                  className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <Trash2 className="w-4 h-4" /> Remove this vehicle
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Inspection Modal */}
        {showAddInspection && selectedFleetVehicle && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowAddInspection(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[#d2d2d7]">
                <h2 className="text-lg font-semibold text-[#1d1d1f]">Add Inspection Record</h2>
                <p className="text-sm text-[#86868b]">{selectedFleetVehicle.registration}</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Mileage (km)</label>
                  <input
                    type="number"
                    value={newInspection.mileage}
                    onChange={(e) => setNewInspection({ ...newInspection, mileage: e.target.value })}
                    placeholder="e.g. 45000"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Exterior Condition</label>
                    <select
                      value={newInspection.exterior_condition}
                      onChange={(e) => setNewInspection({ ...newInspection, exterior_condition: e.target.value })}
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    >
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Interior Condition</label>
                    <select
                      value={newInspection.interior_condition}
                      onChange={(e) => setNewInspection({ ...newInspection, interior_condition: e.target.value })}
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    >
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Body Damage Notes</label>
                  <input
                    type="text"
                    value={newInspection.body_damage}
                    onChange={(e) => setNewInspection({ ...newInspection, body_damage: e.target.value })}
                    placeholder="Describe any body damage"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Warning Lights</label>
                  <input
                    type="text"
                    value={newInspection.warning_lights}
                    onChange={(e) => setNewInspection({ ...newInspection, warning_lights: e.target.value })}
                    placeholder="Any warning lights on?"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Tyre Condition</label>
                  <input
                    type="text"
                    value={newInspection.tyre_condition}
                    onChange={(e) => setNewInspection({ ...newInspection, tyre_condition: e.target.value })}
                    placeholder="e.g. Good, needs replacement"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Notes</label>
                  <textarea
                    value={newInspection.notes}
                    onChange={(e) => setNewInspection({ ...newInspection, notes: e.target.value })}
                    placeholder="Additional inspection notes"
                    rows={2}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A] resize-none"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-[#d2d2d7] flex gap-3">
                <button
                  onClick={() => setShowAddInspection(false)}
                  className="flex-1 py-3 rounded-xl font-medium text-[#424245] hover:bg-[#f5f5f7] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddInspection}
                  disabled={addingInspection}
                  className="flex-1 bg-[#C41E3A] text-white py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors disabled:opacity-50"
                >
                  {addingInspection ? 'Saving...' : 'Save Inspection'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Service Record Modal */}
        {showAddService && selectedFleetVehicle && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowAddService(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[#d2d2d7]">
                <h2 className="text-lg font-semibold text-[#1d1d1f]">Add Service Record</h2>
                <p className="text-sm text-[#86868b]">{selectedFleetVehicle.registration}</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Service Type</label>
                  <select
                    value={newService.service_type}
                    onChange={(e) => setNewService({ ...newService, service_type: e.target.value })}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  >
                    <option value="valet">Valet / Detailing</option>
                    <option value="mechanical">Mechanical Repair</option>
                    <option value="body_repair">Panel Beating / Body Repair</option>
                    <option value="maintenance">Maintenance Service</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Description</label>
                  <textarea
                    value={newService.description}
                    onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                    placeholder="Describe the service performed"
                    rows={2}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A] resize-none"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Mileage (km)</label>
                    <input
                      type="number"
                      value={newService.mileage}
                      onChange={(e) => setNewService({ ...newService, mileage: e.target.value })}
                      placeholder="e.g. 45000"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Cost (R)</label>
                    <input
                      type="number"
                      value={newService.cost}
                      onChange={(e) => setNewService({ ...newService, cost: e.target.value })}
                      placeholder="e.g. 900"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Booking Reference (optional)</label>
                  <input
                    type="text"
                    value={newService.booking_reference}
                    onChange={(e) => setNewService({ ...newService, booking_reference: e.target.value })}
                    placeholder="e.g. FLT-20260101-ABC123"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Notes</label>
                  <textarea
                    value={newService.notes}
                    onChange={(e) => setNewService({ ...newService, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows={2}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A] resize-none"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-[#d2d2d7] flex gap-3">
                <button
                  onClick={() => setShowAddService(false)}
                  className="flex-1 py-3 rounded-xl font-medium text-[#424245] hover:bg-[#f5f5f7] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddServiceRecord}
                  disabled={addingService}
                  className="flex-1 bg-[#C41E3A] text-white py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors disabled:opacity-50"
                >
                  {addingService ? 'Saving...' : 'Save Service Record'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Workshop Staff Modal */}
        {showAddStaff && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddStaff(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="add-workshop-staff-modal"
            >
              <div className="p-6 border-b border-[#d2d2d7]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">Add Workshop Staff</h2>
                  <button onClick={() => setShowAddStaff(false)} className="text-[#86868b] hover:text-[#1d1d1f]">
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={newStaff.name}
                    onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    placeholder="john@maafrica.co.za"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Password *</label>
                  <div className="relative">
                    <input
                      type="password"
                      value={newStaff.password}
                      onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                      placeholder="Create a password"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b]" />
                  </div>
                  <p className="text-xs text-[#86868b] mt-1">Share this password with the staff member</p>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Role</label>
                  <select
                    value={newStaff.role}
                    onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  >
                    <option value="technician">Technician</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-[#d2d2d7]">
                <button
                  onClick={handleAddWorkshopStaff}
                  disabled={addingStaff || !newStaff.name || !newStaff.email || !newStaff.password}
                  className="w-full bg-[#C41E3A] text-white py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingStaff ? 'Adding...' : 'Add Staff Member'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Add Gallery Modal */}
        {showAddGallery && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddGallery(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="add-gallery-modal"
            >
              <div className="p-6 border-b border-[#d2d2d7]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">Add Gallery Item</h2>
                  <button
                    onClick={() => setShowAddGallery(false)}
                    className="text-[#86868b] hover:text-[#1d1d1f]"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Title *</label>
                  <input
                    type="text"
                    value={newGalleryItem.title}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, title: e.target.value })}
                    placeholder="e.g., BMW M4 Full Detail"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Before Image URL *</label>
                  <input
                    type="url"
                    value={newGalleryItem.before_image}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, before_image: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">After Image URL *</label>
                  <input
                    type="url"
                    value={newGalleryItem.after_image}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, after_image: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Vehicle Make</label>
                    <input
                      type="text"
                      value={newGalleryItem.vehicle_make}
                      onChange={(e) => setNewGalleryItem({ ...newGalleryItem, vehicle_make: e.target.value })}
                      placeholder="e.g., BMW"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#86868b] mb-1">Vehicle Model</label>
                    <input
                      type="text"
                      value={newGalleryItem.vehicle_model}
                      onChange={(e) => setNewGalleryItem({ ...newGalleryItem, vehicle_model: e.target.value })}
                      placeholder="e.g., M4"
                      className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Service Type</label>
                  <input
                    type="text"
                    value={newGalleryItem.service_type}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, service_type: e.target.value })}
                    placeholder="e.g., Prestige Detail"
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A]"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-[#86868b] mb-1">Description</label>
                  <textarea
                    value={newGalleryItem.description}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, description: e.target.value })}
                    placeholder="Brief description of the work done"
                    rows={2}
                    className="w-full px-4 py-2 bg-[#f5f5f7] border border-transparent rounded-lg text-sm focus:outline-none focus:border-[#C41E3A] resize-none"
                  />
                </div>
                
                <button
                  onClick={handleAddGalleryItem}
                  className="w-full py-3 bg-[#C41E3A] text-white rounded-xl font-medium hover:bg-[#a01830] transition-colors"
                  data-testid="submit-gallery-btn"
                >
                  Add to Gallery
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Booking Detail Modal */}
        {selectedBooking && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedBooking(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="booking-detail-modal"
            >
              <div className="p-6 border-b border-[#d2d2d7]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#1d1d1f]">Booking Details</h2>
                  <button
                    onClick={() => setSelectedBooking(null)}
                    className="text-[#86868b] hover:text-[#1d1d1f]"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Customer Info */}
                <div>
                  <h3 className="text-xs font-medium text-[#86868b] mb-3">CUSTOMER</h3>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-[#1d1d1f]">{selectedBooking.customer_name}</p>
                    <div className="flex items-center gap-2 text-sm text-[#424245]">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${selectedBooking.customer_email}`} className="hover:text-[#D4AF37]">
                        {selectedBooking.customer_email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#424245]">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${selectedBooking.customer_phone}`} className="hover:text-[#D4AF37]">
                        {selectedBooking.customer_phone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Service Info */}
                <div>
                  <h3 className="text-xs font-medium text-[#86868b] mb-3">SERVICE</h3>
                  <div className="bg-[#f5f5f7] rounded-xl p-4 space-y-2">
                    <p className="text-sm font-medium text-[#1d1d1f]">{selectedBooking.package_name}</p>
                    <div className="flex items-center gap-2 text-sm text-[#424245]">
                      <Car className="w-4 h-4" />
                      {selectedBooking.vehicle_make} {selectedBooking.vehicle_model} ({selectedBooking.vehicle_type})
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#424245]">
                      <MapPin className="w-4 h-4" />
                      {selectedBooking.service_location === 'mobile' ? 'Mobile Service' : 'Studio Visit'}
                    </div>
                    {selectedBooking.address && (
                      <p className="text-xs text-[#86868b] ml-6">{selectedBooking.address}</p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-[#424245]">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedBooking.preferred_date)} at {selectedBooking.preferred_time}
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div>
                  <h3 className="text-xs font-medium text-[#86868b] mb-3">PAYMENT</h3>
                  <div className="flex items-center justify-between p-4 bg-[#1d1d1f] rounded-xl">
                    <div>
                      <p className="text-xs text-[#86868b]">Total Amount</p>
                      <p className="text-xl font-semibold text-[#D4AF37]">R{selectedBooking.total_price?.toLocaleString()}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${paymentStatusColors[selectedBooking.payment_status]?.bg} ${paymentStatusColors[selectedBooking.payment_status]?.text}`}>
                      {selectedBooking.payment_status}
                    </span>
                  </div>
                </div>

                {/* Status Update */}
                <div>
                  <h3 className="text-xs font-medium text-[#86868b] mb-3">UPDATE STATUS</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => updateBookingStatus(selectedBooking.id, key)}
                        disabled={updatingStatus || selectedBooking.status === key}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedBooking.status === key
                            ? `${statusColors[key]?.bg} ${statusColors[key]?.text} border-2 ${statusColors[key]?.border}`
                            : 'bg-[#f5f5f7] text-[#424245] hover:bg-[#e8e8ed]'
                        } ${updatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`status-btn-${key}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                {selectedBooking.notes && (
                  <div>
                    <h3 className="text-xs font-medium text-[#86868b] mb-2">CUSTOMER NOTES</h3>
                    <p className="text-sm text-[#424245] bg-[#f5f5f7] rounded-lg p-3">{selectedBooking.notes}</p>
                  </div>
                )}

                {/* Timestamps */}
                <div className="text-xs text-[#86868b] pt-4 border-t border-[#d2d2d7]">
                  <p>Created: {formatDateTime(selectedBooking.created_at)}</p>
                  {selectedBooking.updated_at && (
                    <p>Updated: {formatDateTime(selectedBooking.updated_at)}</p>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </main>
  );
}
